import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, PhoneIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { CtaSection } from '../components/CtaSection';
import { SectionHeading } from '../components/SectionHeading';
import { services } from '../data/services';
import { business } from '../data/site';
import { ui } from '../data/ui';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import { buttonClass } from '../utils/styles';
const serviceImages: Record<string, string> = {
  'interior-painting': 'https://cdn.magicpatterns.com/uploads/dMRi6Kungcez39ZAkavnKW/Foto_pintura_ascensor.jpg',
  'exterior-painting': 'https://cdn.magicpatterns.com/uploads/n2p2cNV2SacYHyKKJAHCfV/Pintura_casa_3.jpg',
  drywall: 'https://cdn.magicpatterns.com/patterns/generated-images/259e578b-ab34-4861-bade-65d61e9ea027.jpg',
  'lvp-flooring': 'https://cdn.magicpatterns.com/patterns/generated-images/17bb2718-2ccf-442b-b8a8-cb23d8b4b13b.jpg',
  remodeling: 'https://cdn.magicpatterns.com/uploads/qWbsV2rDWz3Ks637Zaxvkw/Despues_cocina.jpg',
  'laminate-flooring': 'https://cdn.magicpatterns.com/patterns/generated-images/45e760f3-3cd8-43a0-852a-85e5ad97ea2d.jpg'
};
export function Services() {
  const {
    t,
    lang
  } = useLanguage();
  useSeo({
    title: lang === 'en' ? 'Painting, Drywall, Flooring & Remodeling Services in Dallas TX | M&S Multiservices' : 'Servicios de Pintura, Drywall, Pisos y Remodelación en Dallas TX | M&S Multiservices',
    description: lang === 'en' ? 'Interior & exterior painting, drywall installation and repair, laminate and LVP flooring, carpet, framing, remodeling, demolition and junk removal across Dallas-Fort Worth.' : 'Pintura interior y exterior, instalación y reparación de drywall, pisos laminados y LVP, alfombra, framing, remodelación, demolición y retiro de basura en Dallas-Fort Worth.'
  });
  return <>
      <PageHeader eyebrow={t({
      en: 'Our services',
      es: 'Nuestros servicios'
    })} title={t({
      en: 'Painting, drywall, flooring and remodeling done right the first time',
      es: 'Pintura, drywall, pisos y remodelación bien hechos desde el primer día'
    })} description={t({
      en: 'One licensed, insured crew for the whole job — no subcontractor hand-offs, no surprises on the invoice. Pricing is always quoted in person after we see the work.',
      es: 'Un solo equipo licenciado y asegurado para todo el trabajo — sin subcontratistas ni sorpresas en la factura. El precio se cotiza en persona después de ver el trabajo.'
    })} data-id="element-515" data-mp="1cb6nohwyunt5" />

      <nav aria-label={t({
      en: 'Jump to a service',
      es: 'Ir a un servicio'
    })} className="bg-mist" data-id="element-516" data-mp="201aqin2byjj3">
        <ul className="mx-auto flex max-w-7xl flex-wrap gap-2 px-6 py-5" data-id="element-517" data-mp="2e0k2tbnf0ths">
          {services.map(service => <li key={service.slug} data-id="element-518" data-mp="293jxj2ye3urq">
              <a href={`#${service.slug}`} className="inline-flex items-center rounded-full border border-navy/15 bg-white px-3.5 py-1.5 text-sm font-medium text-navy transition-colors duration-150 ease-out-expo hover:border-gold hover:bg-gold-50" data-id="element-519" data-mp="290e8fnprgsw0">
                {t(service.name)}
              </a>
            </li>)}
        </ul>
      </nav>

      <div className="bg-white" data-id="element-520" data-mp="2nqshot9ofafu">
        <div className="mx-auto max-w-7xl divide-y divide-navy/10 px-6" data-id="element-521" data-mp="29x18jlzuxskn">
          {services.map((service, index) => {
          const image = serviceImages[service.slug];
          const reverse = index % 2 === 1;
          return <section key={service.slug} id={service.slug} aria-labelledby={`${service.slug}-title`} className="py-14 sm:py-20" data-id="element-522" data-mp="2016ii5tb0v7u" data-mpi={index}>
                <div className={['grid gap-10 lg:gap-14', image ? 'lg:grid-cols-2 lg:items-center' : 'lg:grid-cols-[1fr_1fr]'].join(' ')} data-id="element-523" data-mp="1r0rtavd7yvz2">
                  <div className={reverse && image ? 'lg:order-2' : undefined} data-id="element-524" data-mp="1ixh79wslro5f">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy" data-id="element-525" data-mp="1e797w7wn5dvu">
                      <service.icon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-526" data-mp="1a5nm1x24pp9i" />
                    </span>
                    <h2 id={`${service.slug}-title`} className="mt-5 font-display text-3xl leading-tight text-navy sm:text-4xl" data-id="element-527" data-mp="1573zbufpo9g3">
                      {t(service.name)}
                    </h2>
                    <p className="mt-4 text-lg leading-relaxed text-ink/75" data-id="element-528" data-mp="vahfn8z4fofn">
                      {t(service.description)}
                    </p>
                    <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-529" data-mp="2pmxrcpaorurw">
                      <Link to="/contact" className={buttonClass('gold', 'md')} data-id="element-530" data-mp="2lkuebw8dci4i">
                        {t(ui.freeEstimate)}
                      </Link>
                      <a href={business.phoneHref} className={buttonClass('outline', 'md')} data-id="element-531" data-mp="7cjsbt68cl26">
                        <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-532" data-mp="cav100ksgpit" />
                        {business.phoneDisplay}
                      </a>
                    </div>
                  </div>

                  <div className={reverse && image ? 'lg:order-1' : undefined} data-id="element-533" data-mp="l36jn09nv8o8">
                    {image ? <img src={image} alt={t(service.name)} className="aspect-[4/3] w-full rounded-xl object-cover shadow-card" loading="lazy" data-id="element-534" data-mp="3klfuzv30e3pe" /> : null}
                    <ul className={['grid gap-x-8 gap-y-3 sm:grid-cols-2', image ? 'mt-6' : 'rounded-xl bg-mist p-7'].join(' ')} data-id="element-535" data-mp="2fjmnjdalkbl">
                      {service.bullets.map(bullet => <li key={bullet.en} className="flex items-start gap-2.5" data-id="element-536" data-mp="39ugtgw0cr1ux">
                          <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-gold-dark" aria-hidden="true" data-id="element-537" data-mp="35twwbsfs3y8s" />
                          <span className="text-[0.95rem] text-ink/80" data-id="element-538" data-mp="3ewekicezv0yf">{t(bullet)}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </section>;
        })}
        </div>
      </div>

      <section className="bg-mist" aria-labelledby="handyman-heading" data-id="element-539" data-mp="30w3nssr06s5c">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20" data-id="element-540" data-mp="2w4vgq3d5bivc">
          <SectionHeading id="handyman-heading" eyebrow={t({
          en: 'Also available',
          es: 'También disponible'
        })} title={t({
          en: 'Handyman services & dumpster rental',
          es: 'Servicios de handyman y renta de dumpster'
        })} description={t({
          en: 'Door adjustments, trim repairs, mounting, patching, fixture swaps, property cleanouts and dumpster drop-off. Small jobs get the same crew and the same warranty as the big ones.',
          es: 'Ajuste de puertas, reparación de molduras, instalaciones, parches, cambio de accesorios, limpieza de propiedades y entrega de dumpster. Los trabajos pequeños reciben el mismo equipo y la misma garantía.'
        })} data-id="element-541" data-mp="2w3ajg5zg5lts" />
        </div>
      </section>

      <CtaSection data-id="element-542" data-mp="16myl43jncglw" />
    </>;
}